"use client";
import { Col, Row } from "@/utils/bootstrap";
import AllCartItems from "./all-cart-items/AllCartItems";
import CartButtons from "./cart-buttons/CartButtons";
import Promo from "./promo/Promo";
import {
  useGetCartQuery,
  useGetUserLocation,
} from "@/api/AppApi/services/cart";
import { useIsMobile } from "@/hooks/useIsMobile";
import GeneralApiLoader from "../../lib/loaders/GeneralApiLoader";
import CartEmpty from "./CartEmpty";
import { TabbyPromo } from "../../lib/Tabby";
import useCanTabby from "@/hooks/useCanTabby";
import ViewCartPixel from "./ViewCartPixel";
import useSellingMode from "@/hooks/useSellingMode";
import { useEffect } from "react";
import { useRouter } from "../../lib/navigation/useRouter";
import { useTranslation } from "@/hooks/localization/useTranslations";
import CustomButton, { BtnRadius, BtnSizes, BtnTypes } from "../../lib/CustomButton";

import CartExchangeSection from "./exchange/CartExchangeSection";
import CartCrossSell from "./crosssell/CartCrossSell";
import { updateGetCartQueryData } from "@/api/AppApi/services/cart/cart-helpers";
import { useQueryClient } from "@tanstack/react-query";

interface Props {
  cartBuilderData?: any;
}

const Cart: React.FC<Props> = ({ cartBuilderData }) => {
  const t = useTranslation();
  const queryClient = useQueryClient();

  useEffect(() => {
    // Seed the cart cache from the SSR builder payload — but NEVER with an empty
    // cart. That payload belongs to whichever `Agent-Id` the SSR request carried,
    // and a request that arrives without the cookie gets a brand-new empty cart
    // back; writing that over the shopper's real cart (which `useGetCartQuery`
    // then pins, staleTime: Infinity) is what made carts "go empty" on iOS
    // in-app browsers. An empty SSR cart tells us nothing, so drop it and let the
    // query below fetch with the visitor's own identity.
    if (cartBuilderData?.items?.length) {
      updateGetCartQueryData(queryClient, {
        message: { text: "Operation done successfully", type: "success" },
        result: cartBuilderData,
      });
    }
  }, [cartBuilderData, queryClient]);

  // The cart page is the one place that must always agree with the backend, so it
  // revalidates on mount instead of trusting whatever landed in the cache first
  // (the global default for the header badge is staleTime: Infinity).
  const getCart = useGetCartQuery({ staleTime: 0 });
  const { enableSells, isFetching } = useSellingMode();
  const router = useRouter();
  const cart = getCart?.data?.result;
  const { items, discount, tax, total, coupon, shipping_charge } = cart ?? {};
  const mobile = useIsMobile();
  const canTabby = useCanTabby();
  const getUserLocation = useGetUserLocation({});
  const userLocationCode = getUserLocation?.data?.country_code;

  useEffect(() => {
    if (isFetching) return;
    if (!enableSells) router.replace("/");
  }, [enableSells, isFetching, router]);

  useEffect(() => {
    if (isFetching) return;
    if (!enableSells) router.replace("/");
  }, [enableSells, isFetching, router]);

  // `isFetching && !cart`, not bare `isFetching`: with staleTime 0 above, a
  // background revalidation on every mount would otherwise replace an already
  // rendered cart with a loader.
  if ((getCart.isFetching && !cart) || isFetching) return <GeneralApiLoader />;
  if (!cart || !items) return null;
  if (items?.length === 0) return <CartEmpty />;
  const mClass = mobile ? "-mobile" : "";
  return (
    <>
      <Row className="m-0 mb-4" style={{ gap: mobile ? 28 : 32 }}>
        <Col as="section" className="p-0" lg={6}>
          <CartExchangeSection mobile={mobile} />
          <AllCartItems cartItems={items} mobile={mobile} />
        </Col>
        <Col as="section" className="p-0" lg={{ span: true }}>
          <div className={`cart-promo${mClass} w-100`}>
            <CartButtons tax={cart?.tax} total={cart?.total} mobile={mobile} />
            <Promo
              promoCode={cart.coupon ? cart.coupon?.code : ""}
              promoDiscount={cart?.coupon ? cart.coupon?.discount : 0}
              mobile={mobile}
              countryCode={userLocationCode!}
            />
            {mobile && (
              <CustomButton
                type={BtnTypes.neutral}
                size={BtnSizes.large}
                radius={BtnRadius.small}
                onClick={() => router.push("/")}
                fullWidth
                className="my-4"
              >
                {t.continue_shopping}
              </CustomButton>
            )}
            {canTabby && <TabbyPromo price={cart?.total} source="cart" />}{" "}
          </div>
        </Col>
      </Row>

      <CartCrossSell />
      <ViewCartPixel
        cartItems={items}
        tax={tax!}
        value={total!}
        coupon={coupon?.code!}
        shipping={shipping_charge!}
        discount={discount!}
      />
    </>
  );
};

export default Cart;
